import React, { useEffect } from 'react';
import './AddEditReferralsRequest.AdditionalInformation.scss';
import { FieldError } from 'v3/Components/Form/FormSection';
import { useReferralRequestForm } from './useReferralRequestForm';
import './AddEditReferralsRequest.ReportQuestions.scss';
import ItemsToggler from 'v3/Components/Form/ItemsToggler';
import { InputActive } from 'v3/Components/Form/InputActive';
import { Clear, Add } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { TitleReadOnly } from 'v3/Components/Form/TitleReadOnly';

type ReportQuestionsProps = {
  form: ReturnType<typeof useReferralRequestForm>;
  fieldErrors?: FieldError[];
};

export const ReportQuestionsActive: React.FC<ReportQuestionsProps> = ({
  form,
  fieldErrors,
}) => {
  const addQuestionRow = () => {
    form.setFieldValue(`additionalQuestions`, [
      ...(form.values.additionalQuestions ?? []),
      {
        value: '',
        id: uuidv4(),
        isSelected: true,
        isAdditional: true,
      },
    ]);
  };

  return (
    <div className="">
      <div>
        <div className="lob-questions-header">
          The questions below have been set as a default for this type of
          referral. These are the questions that the reviewing doctor will be
          asked. You can turn questions on and off, according to the specific
          needs of this case.
        </div>
        <ItemsToggler
          items={form.values.clientReportQuestions
            ?.filter((q, i) => q.value)
            .map((q, i) => ({
              id: q?.id,
              value: q?.value,
              isSelected: q?.isSelected,
              isAdditional: q?.isAdditional,
            }))}
          onItemToggle={(item) => {
            const question = form.values.clientReportQuestions?.find(
              (q) => q?.id === item.id
            );
            if (question) {
              question.isSelected = !question.isSelected;
              // update the clientReportQuestions with the new selected value
              form.setFieldValue('clientReportQuestions', [
                ...(form.values?.clientReportQuestions ?? []),
              ]);
            }
          }}
        />

        <div className="additional-questions-header">
          If the case requires additional questions than those that are provided
          above, you can add a custom question below.
        </div>

        <div className="additional-questions">
          {form.values.additionalQuestions?.map((question, i) => (
            <ReportQuestion
              key={`additionalQuestions[${i}]`}
              uniqueId={`additionalQuestions[${i}]`}
              index={i}
              form={form}
              fieldErrors={fieldErrors}
            />
          ))}
          <div
            onClick={() => addQuestionRow()}
            style={{
              marginBottom: 40,
            }}
            className="add-item-button"
          >
            <Add />
            <span>Add Additional Question</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportQuestion = ({
  uniqueId,
  index,
  form,
  fieldErrors,
}: {
  uniqueId: string;
  index: number;
  form: ReturnType<typeof useReferralRequestForm>;
  fieldErrors?: FieldError[];
}) => {
  return (
    <div key={index} className="contact-row">
      <div className="contact-fields">
        <InputActive
          id={`${uniqueId}`}
          label="Additional Question"
          value={form.getFieldProps(`${uniqueId}`).value.value ?? ''}
          onChange={(e: any) => {
            form.setFieldValue(`${uniqueId}.value`, e.target.value);
          }}
          error={fieldErrors?.find((f) => f.fieldId === `${uniqueId}`)?.error}
        />
      </div>
      {index > 0 ? (
        <Clear
          className="x-icon"
          onClick={() => {
            form.setFieldValue(
              `additionalQuestions`,
              form.values.additionalQuestions?.filter((_, i) => i !== index)
            );
          }}
        />
      ) : (
        <div className="x-icon"></div>
      )}
    </div>
  );
};

export const ReportQuestionsReadOnly: React.FC<ReportQuestionsProps> = ({
  form,
}) => {
  return (
    <div className="read-only-content">
      <TitleReadOnly>Standard Questions</TitleReadOnly>
      {form.values.clientReportQuestions
        ?.filter((c) => c?.isSelected)
        .map((question, i) => (
          <div key={`clientReportQuestion${i}`} className="read-only-field">
            <span>{question.value}</span>
            <div className="dashed-line"></div>
          </div>
        ))}
      <TitleReadOnly>Additional Question(s)</TitleReadOnly>
      {form.values.additionalQuestions
        ?.filter((c) => !!c.value)
        .map((question, i) => (
          <div key={`additionalQuestions${i}`} className="read-only-field">
            <span>{question.value}</span>
            <div className="dashed-line"></div>
          </div>
        ))}
    </div>
  );
};
